import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Empty, Form, Modal, Space, Table, message } from 'antd';
import { HomeOutlined, PlusOutlined } from '@ant-design/icons';
import { isSalesUser, validateInquiryItem, buildInquiryItem } from '../utils/inquiryHelpers';
import { buildInquiryItemColumns } from '../utils/inquiryItemColumns';
import InquiryHeaderFields from '../components/InquiryHeaderFields';
import InquiryItemModal from '../modals/InquiryItemModal';
import { useI18n } from '../i18n';

const buildHeaderState = (currentUser, leads = [], initialClientId = null) => ({
  createdBy: isSalesUser(currentUser) ? (currentUser?.name || '') : '',
  clientId: initialClientId,
  deliveryAddress: leads.find((lead) => lead.id === initialClientId)?.address || '',
  priority: 'Medium',
  neededBefore: null,
  requestedDeliveryDate: null,
});

export default function InquiryCreatePage({
  leads = [],
  categories = [],
  products = [],
  services = [],
  logisticsItems = [],
  users = [],
  currentUser = null,
  initialClientId = null,
  onSave,
  onCancel,
  showModal,
}) {
  const { t } = useI18n();

  const [header, setHeader] = useState(() => buildHeaderState(currentUser, leads, initialClientId));
  const [deliveryAddressTouched, setDeliveryAddressTouched] = useState(false);
  const [headerErrors, setHeaderErrors] = useState({});

  const [items, setItems] = useState([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemsError, setItemsError] = useState('');

  const updateHeaderField = (field, value) => setHeader((prev) => ({ ...prev, [field]: value }));

  // When "+ New Customer" creates a lead while this page is open, auto-select
  // the newly created lead once it lands in `leads` (no other way to learn
  // its id, since the create-lead modal is generic and shared app-wide).
  const awaitingNewClientRef = useRef(false);
  const prevLeadsCountRef = useRef(leads.length);
  useEffect(() => {
    if (awaitingNewClientRef.current && leads.length > prevLeadsCountRef.current) {
      const newLead = leads[leads.length - 1];
      handleClientChange(newLead.id);
      awaitingNewClientRef.current = false;
    }
    prevLeadsCountRef.current = leads.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads]);

  const handleClientChange = (id) => {
    setHeader((prev) => {
      const next = { ...prev, clientId: id };
      if (!deliveryAddressTouched) {
        const lead = leads.find((l) => l.id === id);
        next.deliveryAddress = lead?.address || '';
      }
      return next;
    });
  };

  const handleDeliveryAddressChange = (field, value) => {
    if (field === 'deliveryAddress') setDeliveryAddressTouched(true);
    updateHeaderField(field, value);
  };

  const handleSaveItem = (itemData) => {
    const validationErrors = validateInquiryItem({
      categoryId: itemData.categoryId,
      quantity: itemData.quantity,
      specifications: itemData.specifications,
    });
    if (Object.keys(validationErrors).length > 0) {
      message.warning(t('Please complete the required product fields'));
      return;
    }
    const builtItem = buildInquiryItem(itemData);
    setItems((prev) => (
      editingItem
        ? prev.map((item) => (item.id === editingItem.id ? { ...builtItem, id: editingItem.id } : item))
        : [...prev, builtItem]
    ));
    setItemsError('');
    setItemModalOpen(false);
    setEditingItem(null);
  };

  const handleDeleteItem = (record) => {
    Modal.confirm({
      title: t('Delete this product?'),
      content: t('Are you sure you want to delete this entry?'),
      okText: t('Delete'),
      cancelText: t('Cancel'),
      onOk: () => setItems((prev) => prev.filter((item) => item.id !== record.id)),
    });
  };

  const handleAddCustomer = () => {
    awaitingNewClientRef.current = true;
    showModal?.('createLead');
  };

  const handleSave = () => {
    const nextHeaderErrors = {};
    if (!header.createdBy) nextHeaderErrors.createdBy = t('Created by is required');
    if (!header.clientId) nextHeaderErrors.clientId = t('Client name is required');
    if (Object.keys(nextHeaderErrors).length > 0) {
      setHeaderErrors(nextHeaderErrors);
      message.warning(t('Please fill in the required header fields'));
      return;
    }
    setHeaderErrors({});

    if (items.length === 0) {
      setItemsError(t('Add at least one product'));
      message.warning(t('Add at least one product'));
      return;
    }
    setItemsError('');

    const lead = leads.find((l) => l.id === header.clientId);

    onSave?.({
      clientId: header.clientId,
      client: lead?.name || '',
      createdBy: header.createdBy,
      deliveryAddress: header.deliveryAddress,
      priority: header.priority,
      neededBefore: header.neededBefore ? header.neededBefore.format('YYYY-MM-DD') : null,
      requestedDeliveryDate: header.requestedDeliveryDate ? header.requestedDeliveryDate.format('YYYY-MM-DD') : null,
      items,
    });
  };

  const itemColumns = buildInquiryItemColumns({
    t,
    onEdit: (record) => { setEditingItem(record); setItemModalOpen(true); },
    onDelete: handleDeleteItem,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Button icon={<HomeOutlined />} onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button type="primary" onClick={handleSave}>
          {t('Create Inquiry')}
        </Button>
      </div>

      <Card title={t('New Inquiry')} style={{ borderRadius: 16 }}>
        <Form layout="vertical">
          <InquiryHeaderFields
            value={header}
            onChange={handleDeliveryAddressChange}
            onClientChange={handleClientChange}
            leads={leads}
            users={users}
            errors={headerErrors}
            onAddCustomer={handleAddCustomer}
          />
        </Form>
      </Card>

      <Card
        title={t('Products')}
        extra={(
          <Button icon={<PlusOutlined />} onClick={() => { setEditingItem(null); setItemModalOpen(true); }}>
            {t('Add Product')}
          </Button>
        )}
        style={{ borderRadius: 16 }}
      >
        {items.length === 0 ? (
          <Empty description={itemsError || t('No products added yet')}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); setItemModalOpen(true); }}>
              {t('Add Product')}
            </Button>
          </Empty>
        ) : (
          <Table
            rowKey="id"
            dataSource={items}
            columns={itemColumns}
            pagination={false}
            size="middle"
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>

      <Space style={{ justifyContent: 'flex-end', width: '100%', display: 'flex' }}>
        <Button onClick={onCancel}>{t('Cancel')}</Button>
        <Button type="primary" onClick={handleSave}>{t('Create Inquiry')}</Button>
      </Space>

      <InquiryItemModal
        open={itemModalOpen}
        onCancel={() => { setItemModalOpen(false); setEditingItem(null); }}
        onSave={handleSaveItem}
        item={editingItem}
        categories={categories}
        products={products}
        services={services}
        logisticsItems={logisticsItems}
      />
    </div>
  );
}
